import { describeAuthError, errorCode } from '@/lib/authErrors'
import { getApi } from '@/stores/api'
import {
  getTenant,
  getTenants,
  setIsSwitchingTenant,
  setSessionToken,
  setTenant,
} from '@/stores/session'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import i18n from 'i18next'
import { toast } from 'sonner'

/**
 * PET-2999 changed what this slug means.
 *
 * It used to be "the tenant my session is bound to", so reaching a different
 * one required minting a new session. Now that the session binds to the
 * company's own tenant, the slug identifies the OFFICE a quote belongs to, and
 * the company reads every office it holds an active partnership with. Nothing
 * needs switching, and for a company session the slug will essentially never
 * equal the session tenant's.
 *
 * So an unrecognised slug is **passed through** rather than redirected. The
 * server is the authority: every handler scopes by the caller's readable
 * offices and answers 404 otherwise. Checking it here as well meant fetching
 * the office list inside `beforeLoad` on every navigation into this subtree —
 * blocking first paint on a round trip to re-derive something the next request
 * establishes anyway.
 *
 * The switch path survives underneath for a legacy contractor whose membership
 * the backfill has not moved: they are still bound to an office, and holding
 * two of them still needs a new session to move between.
 *
 * It swaps on any SIBLING membership, so it has to ask what the current session
 * is first — see `SUP-17` and the contractor early-return below.
 */
export const Route = createFileRoute('/_contractor/$tenantSlug')({
  beforeLoad: async ({ params }) => {
    const tenant = getTenant()
    if (!tenant) {
      throw redirect({ to: '/login' })
    }

    if (params.tenantSlug === tenant.slug) {
      return
    }

    // Never swap OUT of a company session. Every row in the merged list links
    // to `/$officeSlug/quotes/$quoteNumber` — by construction never the
    // company's own slug — so without this, one click on a quote would trade a
    // session that reads every office for one bound to a single office, and the
    // only way back would be to log in again. The company already reads that
    // office through its `customer` partnership; there is nothing to switch to.
    //
    // `SUP-16` means nobody holds both a company and a legacy office membership
    // today, so this is belt-and-braces until PET-3001 can create that pair.
    // An unknown type falls through to the swap deliberately: that is the
    // pre-PET-2999 behaviour, and breaking a legacy contractor's office switch
    // would be the worse failure.
    if (tenant.type === 'contractor') {
      return
    }

    // Only a legacy sibling membership needs a session swap. Anything else is
    // either a partner office (readable as-is) or unreadable, and the server
    // decides which.
    const targetTenant = getTenants().find((t) => t.slug === params.tenantSlug)
    if (!targetTenant) {
      return
    }

    setIsSwitchingTenant(true)
    try {
      const api = getApi()
      const res = await api['portal-auth'].contractor['switch-tenant'].$post({
        json: { tenantId: targetTenant.id },
      })

      const data = await res.json()
      setSessionToken(data.sessionToken)
      setTenant(data.tenant)
    } catch (e) {
      // `getApi()` throws the error BODY rather than returning a non-ok
      // Response, so the 403 arrives here — an `!res.ok` branch above would
      // never run, and the specific message would never reach the user.
      toast.error(describeAuthError(i18n.t, errorCode(e), i18n.t('shell:switchTenant.failed')))
      throw redirect({ to: '/quotes' })
    } finally {
      setIsSwitchingTenant(false)
    }
  },
  component: Outlet,
})
